import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useRouter } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';

import { Button } from '@sg/garnish';

import {
  Bundle,
  type BundleContentHandle,
  type BundleSelectProduct,
  useBundleMachine,
  useInitBundleMachine,
} from '@order/components-menu';
import { useFeatureFlag } from '@order/LaunchDarkly';

import { NotFoundView } from '../../components';
import { useNavigateBack } from '../../navigation';
import { useBundleRemoteData } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const BundleScreen = () => {
  const isBundleEnabled = useFeatureFlag('CELS-5880-order-product-bundles-enabled');
  const navigateBack = useNavigateBack();

  if (!isBundleEnabled) {
    return (
      <Bundle.Container onDismiss={navigateBack}>
        <NotFoundView withoutFooter />

        <Bundle.CloseButton onPress={navigateBack} />
      </Bundle.Container>
    );
  }

  return <BundleScreenReady />;
};

// ─────────────────────────────────────────────────────────────────────────────

const BundleScreenReady = () => {
  const router = useRouter();
  const navigateBack = useNavigateBack();
  const { formatMessage } = useIntl();

  const { content, products, productsBySlug, restaurantSlug, hasLocation, isLoading } =
    useBundleRemoteData();

  // ─── Refs ────────────────────────────────────────────────────────────

  const contentRef = useRef<BundleContentHandle>(null);

  // ─── Machine ─────────────────────────────────────────────────────────

  const handleProductConfirmed = useCallback(() => {
    contentRef.current?.scrollToEnd();
  }, []);

  const bundleActorRef = useInitBundleMachine({
    actions: { onProductConfirmed: handleProductConfirmed },
  });

  const {
    selectedProducts,
    selectedCount,
    canSelectMore,
    pendingProduct,
    isSelecting,
    openSelect,
    selectProduct,
    confirmSelect,
    closeSelect,
    removeSelected,
  } = useBundleMachine(bundleActorRef);

  // ─── Derived ─────────────────────────────────────────────────────────

  const selectedItems = useMemo(() => {
    return selectedProducts
      .map(({ productSlug, selectionId }) => {
        const product = productsBySlug.get(productSlug);

        if (!product) return;

        return { ...product, selectionId };
      })
      .filter((product) => product !== undefined);
  }, [productsBySlug, selectedProducts]);

  // ─── Handlers ────────────────────────────────────────────────────────

  // NOTE: Stands in until the bundle can actually be added to the bag.
  const handleAddToBag = useCallback(() => {
    alert(selectedItems.map((selectedItem) => selectedItem.id).join(', '));
  }, [selectedItems]);

  const handleSelect = useCallback(
    (product: BundleSelectProduct) => {
      selectProduct({ productSlug: product.slug, restaurantSlug: restaurantSlug ?? '' });
    },
    [restaurantSlug, selectProduct],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  // NOTE: Pushed rather than redirected, so this screen stays underneath and
  // the location search can present as a sheet over it.
  useLayoutEffect(() => {
    if (hasLocation) return;

    router.push('/bundle/search-location');
  }, [hasLocation, router]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <>
      <Bundle.Container onDismiss={navigateBack}>
        {isLoading ? (
          <Bundle.Loading />
        ) : (
          <>
            <Bundle.Content ref={contentRef}>
              <Bundle.Hero imageUrl={content.imageUrl} imageA11y={content.imageA11y} />

              <Bundle.Copy name={content.name} description={content.description} />

              <Bundle.Included selectedCount={selectedCount}>
                {selectedItems.map((selectedItem) => (
                  <Bundle.SelectedItem
                    key={selectedItem.selectionId}
                    product={selectedItem}
                    onRemove={removeSelected}
                  />
                ))}

                {canSelectMore ? (
                  <Bundle.Slot label={content.selectTitle} onPress={openSelect} />
                ) : null}
              </Bundle.Included>
            </Bundle.Content>

            <Bundle.Footer>
              <Button
                size="large-wide"
                accessibilityLabel={formatMessage(messages.addToBag)}
                disabled={selectedCount === 0}
                onPress={handleAddToBag}
              >
                {formatMessage(messages.addToBag)}
              </Button>
            </Bundle.Footer>
          </>
        )}

        <Bundle.CloseButton onPress={navigateBack} />
      </Bundle.Container>

      <Bundle.SelectModal
        isVisible={isSelecting}
        title={content.selectTitle}
        products={products}
        selectedProductSlug={pendingProduct?.productSlug}
        onSelect={handleSelect}
        onClose={closeSelect}
        onContinue={confirmSelect}
      />
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  addToBag: {
    defaultMessage: 'Add to bag',
    description: 'Bundle | Footer | Add to bag CTA',
  },
});
